import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchBooks,
  fetchBookChapters,
  saveBookChapterThunk,
} from "../../redux/admin/book.thunk";
import { fetchCourseCategories } from "../../redux/admin/courseCategory.thunk";
import { selectBooks, selectBookChapters, selectBookLoading } from "../../redux/admin/book.selector";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "ch-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "ch-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const BookChapters = () => {
  const dispatch = useDispatch();

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const books = useSelector(selectBooks);
  const chapters = useSelector(selectBookChapters);
  const loading = useSelector(selectBookLoading);

  const courseCategoryState = useSelector((state) => state.admin?.courseCategory || {});
  const globalCategories = courseCategoryState.courseCategories || courseCategoryState.categories || [];

  /* =====================================================
     SEARCH & FILTER STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [selectedBookId, setSelectedBookId] = useState("");

  /* =====================================================
     MODAL & TABS STATE
  ====================================================== */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // details | range
  const [saving, setSaving] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState(null);

  /* =====================================================
     INITIAL FORM STATE
  ====================================================== */
  const initialFormState = {
    chapter_id: "",
    book_id: "",
    parent_chapter_id: "",
    chapter_no: "",
    chapter_name: "",
    start_page: "",
    end_page: "",
    description: "",
    lichess_url: "",
    status: 1, // 1 = Active, 0 = Inactive
  };

  const [chapterForm, setChapterForm] = useState(initialFormState);

  /* =====================================================
     FETCH LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(fetchCourseCategories());
  }, [dispatch]);

  // Set default category filter once categories load
  useEffect(() => {
    if (globalCategories.length > 0 && !selectedCategoryFilter) {
      const firstId = String(globalCategories[0].id || globalCategories[0].course_categories_id);
      setSelectedCategoryFilter(firstId);
    }
  }, [globalCategories, selectedCategoryFilter]);

  // Fetch books when category filter changes
  useEffect(() => {
    if (selectedCategoryFilter) {
      dispatch(fetchBooks(selectedCategoryFilter));
    }
  }, [dispatch, selectedCategoryFilter]);

  // Auto-select first book when books list updates
  useEffect(() => {
    if (books.length > 0 && (!selectedBookId || !books.some(b => String(b.book_id) === String(selectedBookId)))) {
      setSelectedBookId(String(books[0].book_id));
    } else if (books.length === 0) {
      setSelectedBookId("");
    }
  }, [books]);

  // Fetch chapters when book selection changes
  useEffect(() => {
    if (selectedBookId) {
      dispatch(fetchBookChapters(selectedBookId));
    }
  }, [dispatch, selectedBookId]);

  /* =====================================================
     HANDLERS & HELPERS (SMART PREFIXING)
  ====================================================== */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "parent_chapter_id") {
      if (value) {
        // Parent selected -> auto-generate next sub-chapter number (e.g., 1.1, 1.2)
        const parentCh = chapters.find((c) => String(c.chapter_id) === String(value));
        if (parentCh) {
          const existingChildren = chapters.filter((c) => String(c.parent_chapter_id) === String(value));
          const nextSubNo = `${parentCh.chapter_no}.${existingChildren.length + 1}`;
          setChapterForm((prev) => ({ ...prev, [name]: value, chapter_no: nextSubNo }));
          return;
        }
      } else {
        // Switched back to Main Chapter -> auto-suggest next main chapter number
        const mainChapters = chapters.filter((c) => !c.parent_chapter_id);
        setChapterForm((prev) => ({ ...prev, [name]: value, chapter_no: String(mainChapters.length + 1) }));
        return;
      }
    }

    setChapterForm((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    if (!selectedBookId) {
      toastr.warning("Please select a book first.");
      return;
    }
    setIsEditMode(false);
    setSelectedChapterId(null);

    const mainChapters = chapters.filter((c) => !c.parent_chapter_id);
    setChapterForm({
      ...initialFormState,
      book_id: selectedBookId,
      chapter_no: String(mainChapters.length + 1),
    });
    setActiveTab("details");
    setShowModal(true);
  };

  const openEditModal = (ch) => {
    setIsEditMode(true);
    setSelectedChapterId(ch.chapter_id);
    setChapterForm({
      chapter_id: ch.chapter_id,
      book_id: String(ch.book_id || selectedBookId),
      parent_chapter_id: ch.parent_chapter_id ? String(ch.parent_chapter_id) : "",
      chapter_no: ch.chapter_no || "",
      chapter_name: ch.chapter_name || "",
      start_page: ch.start_page || "",
      end_page: ch.end_page || "",
      description: ch.description || "",
      lichess_url: ch.lichess_url || "",
      status: ch.status ?? 1,
    });
    setActiveTab("details");
    setShowModal(true);
  };

  /* =====================================================
     FORM SUBMIT
  ====================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!chapterForm.chapter_name.trim()) {
      toastr.warning("Chapter name is required.");
      setActiveTab("details");
      return;
    }
    if (!chapterForm.chapter_no) {
      toastr.warning("Chapter number is required.");
      setActiveTab("details");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...chapterForm,
        book_id: selectedBookId,
        parent_chapter_id: chapterForm.parent_chapter_id ? Number(chapterForm.parent_chapter_id) : null,
      };

      await dispatch(saveBookChapterThunk(payload)).unwrap();
      toastr.success(isEditMode ? "Chapter updated successfully." : "New chapter added successfully.");
      setShowModal(false);
      dispatch(fetchBookChapters(selectedBookId));
    } catch (err) {
      toastr.error(err || "Failed to save chapter.");
    } finally {
      setSaving(false);
    }
  };

  // Filter chapters locally by search term
  const filteredChapters = chapters.filter((ch) =>
    (ch.chapter_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(ch.chapter_no || "").includes(searchTerm)
  );

  /* =====================================================
     HIERARCHICAL STRUCTURING & SAFE SORTING
  ====================================================== */
  const getStructuredChapters = () => {
    const mainChapters = filteredChapters.filter((c) => !c.parent_chapter_id);
    const subChapters = filteredChapters.filter((c) => c.parent_chapter_id);

    mainChapters.sort((a, b) => {
      const numA = parseFloat(a.chapter_no) || 0;
      const numB = parseFloat(b.chapter_no) || 0;
      if (numA !== numB) return numA - numB;
      return String(a.chapter_no || "").localeCompare(String(b.chapter_no || ""));
    });

    const structuredList = [];
    mainChapters.forEach((main) => {
      structuredList.push({ ...main, isSub: false });
      
      const children = subChapters.filter((sub) => String(sub.parent_chapter_id) === String(main.chapter_id));
      children.sort((a, b) => {
        const numA = parseFloat(a.chapter_no) || 0;
        const numB = parseFloat(b.chapter_no) || 0;
        if (numA !== numB) return numA - numB;
        return String(a.chapter_no || "").localeCompare(String(b.chapter_no || ""));
      });
      
      children.forEach((child) => {
        structuredList.push({ ...child, isSub: true });
      });
    });

    const handledIds = new Set(structuredList.map((item) => String(item.chapter_id)));
    filteredChapters.forEach((ch) => {
      if (!handledIds.has(String(ch.chapter_id))) {
        structuredList.push({ ...ch, isSub: Boolean(ch.parent_chapter_id) });
      }
    });

    return structuredList;
  };

  const finalStructuredChapters = getStructuredChapters();
  const selectedBookObj = books.find((b) => String(b.book_id) === String(selectedBookId));

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="ch-page">
      <div className="ch-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="ch-header">
          <div>
            <p className="ch-eyebrow">Library &amp; Curriculum · Admin Console</p>
            <h1 className="ch-title">Book Chapters Management</h1>
          </div>
          <button
            type="button"
            className="ch-btn-primary"
            onClick={openAddModal}
            disabled={!selectedBookId}
          >
            <i className="fas fa-plus"></i> Add Chapter / Sub-chapter
          </button>
        </div>

        {/* ── TOP CONTROLS & SELECTION BAR ── */}
        <div className="ch-control-card mb-4">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="ch-label">Course Category Filter</label>
              <select
                className="ch-field"
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              >
                {globalCategories.map((cat) => {
                  const catId = cat.id || cat.course_categories_id;
                  const catName = cat.name || cat.category_name;
                  return (
                    <option key={catId} value={catId}>{catName}</option>
                  );
                })}
              </select>
            </div>
            <div className="col-12 col-md-6">
              <label className="ch-label">Select Book Target *</label>
              <select
                className="ch-field fw-600"
                value={selectedBookId}
                onChange={(e) => setSelectedBookId(e.target.value)}
              >
                <option value="">-- Choose Book from Catalog --</option>
                {books.map((b) => (
                  <option key={b.book_id} value={b.book_id}>
                    {b.title} {b.author ? `by ${b.author}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {selectedBookId ? (
          <>
            {/* ── KPI STRIP ── */}
            <div className="ch-kpi-grid">
              {[
                { label: "Active book", value: selectedBookObj?.title || "Selected Book" },
                { label: "Total items", value: chapters.length },
                { label: "Active status", value: chapters.filter((c) => c.status == 1).length },
              ].map(({ label, value }) => (
                <div key={label} className="ch-kpi">
                  <span className="ch-kpi-label">{label}</span>
                  <span className="ch-kpi-val text-truncate" style={{ maxWidth: "100%" }}>
                    {loading ? <Skeleton w={60} h={22} r={5} /> : value}
                  </span>
                </div>
              ))}
            </div>

            {/* ── TOOLBAR ── */}
            <div className="ch-toolbar">
              <div className="ch-search">
                <i className="fas fa-search ch-search-icon"></i>
                <input
                  type="text"
                  placeholder="Search chapters or sub-chapters…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* ── DATA TABLE ── */}
            <div className="ch-card">
              <div className="table-responsive">
                <table className="ch-table">
                  <thead>
                    <tr>
                      <th style={{ width: "12%" }}>No. / Sub</th>
                      <th style={{ width: "40%" }}>Chapter / Sub-chapter name</th>
                      <th style={{ width: "15%" }}>Page range</th>
                      <th style={{ width: "13%" }}>Status</th>
                      <th className="text-end" style={{ width: "20%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i} className="ch-row">
                          <td><Skeleton w={24} h={12} /></td>
                          <td>
                            <div className="ch-name-cell">
                              <Skeleton w={28} h={28} r={6} style={{ flexShrink: 0 }} />
                              <Skeleton w="65%" h={12} />
                            </div>
                          </td>
                          <td><Skeleton w={60} h={12} /></td>
                          <td><Skeleton w={60} h={22} r={20} /></td>
                          <td style={{ textAlign: "right" }}><Skeleton w={52} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                        </tr>
                      ))
                    ) : finalStructuredChapters.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="ch-empty-state">
                          <div className="ch-empty-icon-wrap">
                            <i className="fas fa-list-check"></i>
                          </div>
                          <p className="ch-empty-title">No chapters found</p>
                          <span className="ch-empty-desc">Click "Add Chapter" to build the curriculum index for this book.</span>
                        </td>
                      </tr>
                    ) : (
                      finalStructuredChapters.map((ch) => {
                        const isSub = ch.isSub;
                        return (
                          <tr key={ch.chapter_id} className={`ch-row ${isSub ? "ch-sub-row" : ""}`}>
                            <td>
                              <span className={`ch-serial-badge ${isSub ? "sub-badge" : ""}`}>{ch.chapter_no}</span>
                            </td>
                            <td>
                              <div className="ch-name-cell" style={{ paddingLeft: isSub ? "28px" : "0px" }}>
                                <div className="ch-avatar">
                                  <i className={`fas ${isSub ? "fa-level-down-alt" : "fa-bookmark"}`}></i>
                                </div>
                                <div>
                                  <div className="ch-name">
                                    {isSub ? <span className="text-muted me-1">↳</span> : null}
                                    {ch.chapter_name}
                                  </div>
                                  {ch.description && <div className="ch-meta">{ch.description}</div>}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="ch-main-text">
                                {ch.start_page && ch.end_page ? `p. ${ch.start_page} – ${ch.end_page}` : ch.start_page ? `p. ${ch.start_page}` : "—"}
                              </div>
                            </td>
                            <td>
                              <span className={`ch-pill ${ch.status == 1 ? "pill-green" : "pill-coral"}`}>
                                {ch.status == 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="text-end">
                              <div className="d-flex align-items-center justify-content-end gap-2">
                                {ch.lichess_url && (
                                  <a
                                    href={ch.lichess_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="ch-btn-outline lichess-badge-btn"
                                    title="Open Lichess Study"
                                  >
                                    <i className="fas fa-chess-knight text-primary"></i> Study
                                  </a>
                                )}
                                <button type="button" className="ch-btn-outline" onClick={() => openEditModal(ch)}>
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── FOOTER ── */}
              <div className="ch-footer">
                <span className="ch-count">Showing {finalStructuredChapters.length} items</span>
              </div>
            </div>
          </>
        ) : (
          <div className="ch-card text-center p-5">
            <div className="ch-empty-icon-wrap">
              <i className="fas fa-book"></i>
            </div>
            <p className="ch-empty-title">No book selected</p>
            <span className="ch-empty-desc">Please select a book from the dropdown above to view and manage its chapters.</span>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════
          MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div
          className="ch-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}
        >
          <div className="ch-modal">

            {/* Sidebar */}
            <div className="ch-modal-side">
              <div className="ch-modal-side-header">
                <p className="ch-modal-side-title">Item profile</p>
                <p className="ch-modal-side-sub">Curriculum indexing</p>
              </div>
              <nav className="ch-modal-nav">
                {[
                  { key: "details", icon: "fas fa-info-circle", label: "Details & Hierarchy" },
                  { key: "range", icon: "fas fa-book-open", label: "Page range & Notes" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`ch-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Main panel */}
            <form onSubmit={handleSubmit} className="ch-modal-main">
              <div className="ch-modal-header">
                <div>
                  <h2 className="ch-modal-title">{isEditMode ? "Edit chapter / sub-chapter" : "Add chapter or sub-chapter"}</h2>
                  <p className="ch-modal-sub">
                    {isEditMode ? "Update specifications below." : "Configure as main chapter or nested under a parent."}
                  </p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} aria-label="Close" disabled={saving}></button>
              </div>

              <div className="ch-modal-body">
                <div className="row g-3">

                  {/* ── DETAILS TAB ── */}
                  {activeTab === "details" && (
                    <>
                      <div className="col-12">
                        <label className="ch-label">Parent Chapter (Optional)</label>
                        <select
                          name="parent_chapter_id"
                          className="ch-field"
                          value={chapterForm.parent_chapter_id}
                          onChange={handleChange}
                        >
                          <option value="">-- None (This is a Main Chapter) --</option>
                          {chapters
                            .filter((c) => !c.parent_chapter_id && String(c.chapter_id) !== String(chapterForm.chapter_id))
                            .map((mainCh) => (
                              <option key={mainCh.chapter_id} value={mainCh.chapter_id}>
                                Chapter {mainCh.chapter_no}: {mainCh.chapter_name}
                              </option>
                            ))}
                        </select>
                        <span className="text-muted fs-7 mt-1 d-block">Select a main chapter if this is a sub-chapter (e.g. 1.1).</span>
                      </div>

                      <div className="col-md-4">
                        <label className="ch-label">Number / Sub-no *</label>
                        <input
                          type="text"
                          name="chapter_no"
                          className="ch-field fw-700"
                          placeholder="e.g. 1 or 1.1"
                          value={chapterForm.chapter_no}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-md-8">
                        <label className="ch-label">Status</label>
                        <select
                          name="status"
                          className="ch-field"
                          value={chapterForm.status}
                          onChange={handleChange}
                        >
                          <option value={1}>Active</option>
                          <option value={0}>Inactive</option>
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="ch-label">Chapter / Sub-chapter name *</label>
                        <input
                          type="text"
                          name="chapter_name"
                          className="ch-field fw-700 text-dark"
                          placeholder="e.g. Opposition or Outflanking"
                          value={chapterForm.chapter_name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* ── PAGE RANGE & NOTES TAB ── */}
                  {activeTab === "range" && (
                    <>
                      <div className="col-md-6">
                        <label className="ch-label">Start page</label>
                        <input
                          type="number"
                          name="start_page"
                          className="ch-field"
                          placeholder="e.g. 15"
                          value={chapterForm.start_page}
                          onChange={handleChange}
                          min="1"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="ch-label">End page</label>
                        <input
                          type="number"
                          name="end_page"
                          className="ch-field"
                          placeholder="e.g. 28"
                          value={chapterForm.end_page}
                          onChange={handleChange}
                          min="1"
                        />
                      </div>
                      <div className="col-12">
                        <label className="ch-label">Lichess Study URL (Optional)</label>
                        <input
                          type="url"
                          name="lichess_url"
                          className="ch-field"
                          placeholder="https://lichess.org/study/..."
                          value={chapterForm.lichess_url}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-12">
                        <label className="ch-label">Description &amp; notes</label>
                        <textarea
                          rows="3"
                          name="description"
                          className="ch-field"
                          placeholder="Add summary of topics covered..."
                          value={chapterForm.description}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER ── */}
              <div className="ch-modal-footer">
                <button type="button" className="ch-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="ch-btn-primary ch-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Saving changes…" : "Adding item…"}
                    </>
                  ) : (
                    isEditMode ? "Save changes" : "Save item"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes ch-spin { to { transform: rotate(360deg); } }
        @keyframes ch-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes ch-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes ch-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        .ch-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          -webkit-font-smoothing: antialiased;
          color: #1d1d1f;
          font-size: 14px;
        }
        .ch-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        .ch-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .ch-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .ch-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        .ch-control-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; padding: 18px 20px; }

        .ch-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        @media (max-width: 640px) { .ch-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        .ch-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .ch-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .ch-kpi-val { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        .ch-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; flex-wrap: wrap; }
        .ch-search { flex: 1; min-width: 200px; position: relative; }
        .ch-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .ch-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .ch-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .ch-search input::placeholder { color: #aeaeb2; }

        .ch-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: background 0.15s, opacity 0.15s; white-space: nowrap; font-family: inherit;
          min-width: 140px; justify-content: center;
        }
        .ch-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .ch-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .ch-btn-submit { padding: 9px 22px; min-width: 158px; }

        .ch-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .ch-table { width: 100%; border-collapse: collapse; }
        .ch-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px;
          border-bottom: 1px solid #e8e8ed; white-space: nowrap;
        }
        .ch-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .ch-table tbody tr:last-child { border-bottom: none; }
        .ch-row:hover { background: #fafafa; }
        .ch-sub-row { background: #fdfdfd; }
        .ch-table td { padding: 13px 18px; vertical-align: middle; }

        .ch-name-cell { display: flex; align-items: center; gap: 10px; }
        .ch-avatar {
          width: 34px; height: 34px; border-radius: 8px; background: #f5f5f7; border: 1px solid #e8e8ed;
          color: #6e6e73; font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .ch-name { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .ch-meta { font-size: 11.5px; color: #6e6e73; margin-top: 2px; }

        .ch-serial-badge {
          width: 38px; height: 28px; border-radius: 7px; background: #f5f5f7; border: 1px solid #e8e8ed;
          color: #1d1d1f; font-size: 11.5px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;
        }
        .ch-serial-badge.sub-badge { background: #eaf3ff; color: #0071e3; border-color: #d3e6ff; font-size: 11px; }

        .ch-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
        .pill-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-coral { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

        .ch-main-text { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        .ch-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .ch-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        .ch-empty-state { text-align: center; padding: 3rem 1rem; }
        .ch-empty-icon-wrap {
          width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px;
          display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px;
        }
        .ch-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .ch-empty-desc { font-size: 12px; color: #aeaeb2; }

        .ch-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; }
        .ch-count { font-size: 12px; color: #6e6e73; font-weight: 500; }

        .ch-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: ch-overlay-in 0.2s ease;
        }
        .ch-modal {
          width: 100%; max-width: 900px; height: 590px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7;
          box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18); display: flex; overflow: hidden; animation: ch-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1);
        }

        .ch-modal-side { width: 210px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .ch-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .ch-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .ch-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        .ch-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .ch-nav-item {
          display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent;
          font-size: 13px; color: #6e6e73; font-weight: 400; cursor: pointer; text-align: left; width: 100%;
          transition: all 0.12s; font-family: inherit;
        }
        .ch-nav-item i { font-size: 12.5px; width: 14px; text-align: center; flex-shrink: 0; }
        .ch-nav-item span { flex: 1; }
        .ch-nav-item:hover:not(.active):not(:disabled) { background: #ebebf0; color: #3c3c43; }
        .ch-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }

        .ch-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .ch-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .ch-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .ch-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .ch-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .ch-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        .ch-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .ch-field {
          width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f;
          background: #fff; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .ch-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        select.ch-field { cursor: pointer; }

        .ch-btn-cancel {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; font-family: inherit;
        }
        .ch-btn-cancel:hover:not(:disabled) { background: #f5f5f7; color: #3c3c43; }

        @media (max-width: 768px) {
          .ch-modal { flex-direction: column; height: auto; max-height: 90vh; }
          .ch-modal-side { width: 100%; border-right: none; border-bottom: 1px solid #e8e8ed; }
          .ch-modal-side-header { display: none; }
          .ch-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px; }
        }
      `}</style>
    </div>
  );
};

export default BookChapters;